"use client";

import type { CSSProperties, ReactNode } from "react";
import { createContext, useContext, useId, useLayoutEffect, useRef, useState } from "react";

import { DATA_LABEL_CLS, DISPLAY } from "../theme";

/**
 * Drafting kit for the DRW messaging drawings.
 *
 * A self-contained SVG vocabulary in the brand engraving idiom (the `a-*`,
 * `tb-*` and `lead-*` classes from the certified-drawing sheet): hairline
 * strokes at 0.8–1.3px, ink at graded opacities, claret for the one live
 * element, Figtree tracked caps for labels, dimension lines with open
 * arrowheads, leader callouts, a title block and a revision marker. Every
 * colour is a --theme-* var so the sheets re-ink with the palette picker.
 *
 * Text ink tiers: `--ink-82` / `--ink-70` / `--ink-60` / `--ink-55` are the
 * body, secondary, tertiary and quietest text tiers (the names are historic;
 * the mixes are tuned per tone). Every tier keeps ≥ 4.5:1 against its plate
 * — and against an `a-fill.paper` fill — in every palette × tone, which the
 * light teal ink of the seafoam canvas makes the binding constraint on paper
 * (full ink there is only ≈8.6:1). Text inked with the live colour uses
 * `--live-text` (the palette's accent-text role on paper; the accent on the
 * dark tones) rather than `--dark`, so palettes whose dark is not a text
 * colour (orange) stay legible. scripts/concept-contrast-audit.mjs proves
 * the matrix; re-run it after touching any of these.
 *
 * Geometry convention: all sheets are authored on a 1180 × H user-space
 * (H = 620 landscape, 760 tall) and scale to the column. Keep text ≥ 9px in
 * user space so it survives the mobile scale (≈0.3).
 *
 * Motion is rationed: `flow` / `active-arc` (continuous), `m-lift` (a leaf
 * rising and falling), `m-settle` (a needle settling, once) and `m-print`
 * (rows appearing in `--i` order, once). Use each at most once per sheet;
 * all stop under prefers-reduced-motion and under `Plate motion={false}`.
 */

export const DRW_CSS = `
.drw{
  --ink:var(--theme-ink);--dark:var(--theme-dark);--accent:var(--theme-accent);--ground:var(--theme-ground);
  --hair:color-mix(in srgb,var(--theme-ink) 16%,var(--theme-ground));
  --ink-82:color-mix(in srgb,var(--theme-ink) 88%,var(--theme-ground));
  --ink-70:color-mix(in srgb,var(--theme-ink) 82%,var(--theme-ground));
  --ink-60:color-mix(in srgb,var(--theme-ink) 78%,var(--theme-ground));
  --ink-55:color-mix(in srgb,var(--theme-ink) 76%,var(--theme-ground));
  --live-text:var(--theme-accent-text);
  --dot:color-mix(in srgb,var(--theme-ink) 6%,transparent);
  position:relative;
  background:color-mix(in srgb,var(--theme-ground) 60%,#ffffff);
  background-image:radial-gradient(var(--dot) 1px,transparent 1.6px);
  background-size:24px 24px;
  border:1px solid var(--hair);border-radius:3px;
  color:var(--ink);
  font-family:var(--font-figtree),-apple-system,"Helvetica Neue",Arial,sans-serif;
}
.drw::before{content:"";position:absolute;left:-1px;right:-1px;top:-1px;height:4px;background:var(--dark);border-radius:3px 3px 0 0;}
.drw svg{width:100%;height:auto;display:block;}
.drw-scroll{overflow-x:auto;-webkit-overflow-scrolling:touch;}
.drw-scroll:focus-visible{outline:2px solid var(--dark);outline-offset:2px;}
@media (max-width:767px){.drw.sheet svg{min-width:1180px;}}
.drw text{font-family:inherit;}
.drw .a-frame{fill:none;stroke:var(--ink);stroke-opacity:.55;stroke-width:1;}
.drw .a-frame.inner{stroke-opacity:.3;}
.drw .grid-line{fill:none;stroke:var(--ink);stroke-opacity:.06;stroke-width:1;}
.drw .a-crop{fill:none;stroke:var(--ink);stroke-opacity:.6;stroke-width:1;}
.drw .a-line{fill:none;stroke:var(--ink);stroke-opacity:.45;stroke-width:1;stroke-linecap:round;stroke-linejoin:round;}
.drw .a-line.strong{stroke-opacity:.8;stroke-width:1.3;}
.drw .a-line.faint{stroke-opacity:.22;}
.drw .a-line.axis{stroke-opacity:.28;stroke-dasharray:5 4;}
.drw .a-line.hidden-line{stroke-opacity:.35;stroke-dasharray:2.5 4;}
.drw .a-line.accent{stroke:var(--dark);stroke-opacity:.9;stroke-width:1.4;}
.drw .a-line.accent-soft{stroke:var(--dark);stroke-opacity:.45;}
.drw .a-fill{fill:var(--ink);fill-opacity:.05;}
.drw .a-fill.mid{fill-opacity:.1;}
.drw .a-fill.accent{fill:var(--dark);fill-opacity:.08;}
.drw .a-fill.paper{fill:var(--ground);fill-opacity:.9;}
.drw .a-hatch{fill:none;stroke:var(--ink);stroke-opacity:.25;stroke-width:.8;}
.drw .a-ring{fill:none;stroke:var(--ink);stroke-opacity:.5;stroke-width:1.1;}
.drw .a-ring.dash{stroke-dasharray:2.5 4;stroke-opacity:.4;}
.drw .a-ring.faint{stroke-opacity:.22;}
.drw .a-tick{fill:none;stroke:var(--ink);stroke-opacity:.55;stroke-width:.9;stroke-linecap:round;}
.drw .a-tick.major{stroke-width:1.3;stroke-opacity:.82;}
.drw .a-node{fill:var(--ink);fill-opacity:.72;}
.drw .a-node.hollow{fill:var(--ground);stroke:var(--ink);stroke-opacity:.7;stroke-width:1;}
.drw .a-core{fill:var(--dark);}
.drw .a-core.ring{fill:none;stroke:var(--dark);stroke-opacity:.4;stroke-width:1.4;}
.drw .a-num{fill:var(--ink-70);font-size:9px;letter-spacing:.06em;}
.drw .a-label{fill:var(--ink);text-transform:uppercase;letter-spacing:.2em;font-size:12px;}
.drw .a-label.accent{fill:var(--live-text);font-weight:600;}
.drw .a-sub{fill:var(--ink-55);text-transform:uppercase;letter-spacing:.16em;font-size:9px;}
.drw .a-note{fill:var(--ink-60);text-transform:uppercase;letter-spacing:.13em;font-size:9.5px;}
.drw .a-note.ink{fill:var(--ink-82);}
.drw .a-body{fill:var(--ink-82);font-size:11.5px;letter-spacing:.01em;}
.drw .a-dim{fill:none;stroke:var(--ink);stroke-opacity:.5;stroke-width:.8;}
.drw .a-dim-fig{fill:var(--ink-82);font-size:11px;letter-spacing:.06em;}
.drw .a-dim-fig.accent{fill:var(--live-text);font-weight:600;}
.drw .lead{fill:none;stroke:var(--ink);stroke-width:1;stroke-opacity:.7;stroke-dasharray:1.2 3.4;stroke-linecap:round;}
.drw .lead-shelf{fill:none;stroke:var(--ink);stroke-width:.9;stroke-opacity:.55;}
.drw .lead-main{fill:var(--ink);text-transform:uppercase;letter-spacing:.14em;font-size:12px;}
.drw .lead-sub{fill:var(--live-text);text-transform:uppercase;letter-spacing:.13em;font-size:10px;font-weight:600;}
.drw .tb-cell{fill:none;stroke:var(--ink);stroke-opacity:.42;stroke-width:1;}
.drw .tb-head{fill:var(--ink-70);text-transform:uppercase;letter-spacing:.2em;font-size:9.5px;}
.drw .tb-key{fill:var(--ink-55);text-transform:uppercase;letter-spacing:.12em;font-size:9px;}
.drw .tb-val{fill:var(--ink);text-transform:uppercase;letter-spacing:.1em;font-size:10.5px;}
.drw .tb-val.strong{fill:var(--live-text);font-weight:600;}
.drw .rev-tri{fill:none;stroke:var(--dark);stroke-width:1.1;stroke-linejoin:round;}
.drw .rev-tri-num{fill:var(--live-text);font-size:8px;font-weight:600;letter-spacing:.04em;}
.drw .approval-text{fill:var(--live-text);text-transform:uppercase;letter-spacing:.2em;font-size:10.5px;font-weight:600;}
.drw .approval-sub{fill:var(--ink-60);text-transform:uppercase;letter-spacing:.14em;font-size:8.5px;}
.drw .approval-rule{fill:none;stroke:var(--dark);stroke-width:.9;stroke-opacity:.7;}
.drw .mono{font-variant-numeric:tabular-nums;}
.drw .active-arc{fill:none;stroke:var(--dark);stroke-width:3;stroke-linecap:round;stroke-dasharray:22 78;animation:drw-sweep 6s linear infinite;}
.drw .flow{stroke-dasharray:4 6;animation:drw-flow 2.4s linear infinite;}
@keyframes drw-sweep{from{stroke-dashoffset:100}to{stroke-dashoffset:0}}
@keyframes drw-flow{from{stroke-dashoffset:20}to{stroke-dashoffset:0}}

/* ---- rationed motion — each class at most once per sheet ---- */
/* m-lift: a gate leaf lifting ~18 user units, slow, to and fro. */
.drw .m-lift{animation:drw-lift 3s ease-in-out infinite alternate;}
/* m-settle: a needle settling onto its reading — rotates in from −6° with a
   slight overshoot, once. Set transform-origin per element (fill-box). */
.drw .m-settle{transform-box:fill-box;animation:drw-settle 2.4s cubic-bezier(.3,.1,.2,1) both;}
/* m-print: receipt rows appearing one after another; stagger via --i. */
.drw .m-print{animation:drw-print .6s ease-out both;animation-delay:calc(var(--i,0) * 160ms);}
@keyframes drw-lift{from{transform:translateY(0)}to{transform:translateY(-18px)}}
@keyframes drw-settle{0%{transform:rotate(-6deg)}68%{transform:rotate(1.1deg)}86%{transform:rotate(-.35deg)}100%{transform:rotate(0)}}
@keyframes drw-print{from{opacity:0}to{opacity:1}}
@media (prefers-reduced-motion:reduce){.drw .active-arc,.drw .flow,.drw .m-lift,.drw .m-settle,.drw .m-print{animation:none;}}
/* Plate motion={false}: the export pipeline and reduced-motion users get the still sheet. */
.drw.still *{animation:none!important;}
.drw.dark{
  --ink:var(--theme-ground);--dark:var(--theme-accent);--ground:var(--theme-surface-dark);--live-text:var(--theme-accent);
  --hair:color-mix(in srgb,var(--theme-accent) 24%,transparent);
  --ink-82:color-mix(in srgb,var(--theme-ground) 90%,transparent);
  --ink-70:color-mix(in srgb,var(--theme-ground) 82%,transparent);
  --ink-60:color-mix(in srgb,var(--theme-ground) 76%,transparent);
  --ink-55:color-mix(in srgb,var(--theme-ground) 74%,transparent);
  --dot:color-mix(in srgb,var(--theme-accent) 10%,transparent);
  background:var(--theme-surface-dark);
  background-image:radial-gradient(var(--dot) 1px,transparent 1.6px);
  color:var(--theme-ground);
}
.drw.dark::before{background:var(--theme-accent);}
.drw.dark .a-fill.paper{fill:var(--theme-surface-dark);}

/* ---- tone: contrast — heavy ink on lighter paper (print strength) ---- */
.drw.contrast{
  --hair:color-mix(in srgb,var(--theme-ink) 40%,var(--theme-ground));
  --ink-82:var(--theme-ink);--ink-70:color-mix(in srgb,var(--theme-ink) 90%,var(--theme-ground));
  --ink-60:color-mix(in srgb,var(--theme-ink) 84%,var(--theme-ground));--ink-55:color-mix(in srgb,var(--theme-ink) 80%,var(--theme-ground));
  --dot:color-mix(in srgb,var(--theme-ink) 14%,transparent);
  background:color-mix(in srgb,var(--theme-ground) 35%,#ffffff);
  background-image:radial-gradient(var(--dot) 1px,transparent 1.6px);
}
.drw.contrast .grid-line{stroke-opacity:.08;}
.drw.contrast .a-frame{stroke-opacity:.95;stroke-width:1.2;}
.drw.contrast .a-frame.inner{stroke-opacity:.55;}
.drw.contrast .a-crop{stroke-opacity:.95;}
.drw.contrast .a-line{stroke-opacity:.92;stroke-width:1.25;}
.drw.contrast .a-line.strong{stroke-opacity:1;stroke-width:1.7;}
.drw.contrast .a-line.faint{stroke-opacity:.5;}
.drw.contrast .a-line.axis{stroke-opacity:.55;}
.drw.contrast .a-line.hidden-line{stroke-opacity:.7;}
.drw.contrast .a-line.accent{stroke-width:1.8;}
.drw.contrast .a-fill{fill-opacity:.1;}
.drw.contrast .a-fill.mid{fill-opacity:.18;}
.drw.contrast .a-fill.accent{fill-opacity:.14;}
.drw.contrast .a-hatch{stroke-opacity:.55;stroke-width:1;}
.drw.contrast .a-ring{stroke-opacity:.92;stroke-width:1.3;}
.drw.contrast .a-ring.dash{stroke-opacity:.75;}
.drw.contrast .a-ring.faint{stroke-opacity:.5;}
.drw.contrast .a-tick{stroke-opacity:.9;stroke-width:1.1;}
.drw.contrast .a-tick.major{stroke-opacity:1;stroke-width:1.6;}
.drw.contrast .a-node{fill-opacity:.95;}
.drw.contrast .a-dim{stroke-opacity:.9;stroke-width:1;}
.drw.contrast .lead{stroke-opacity:.9;stroke-width:1.1;}
.drw.contrast .tb-cell{stroke-opacity:.85;}
.drw.contrast .lead{stroke-opacity:.95;}

/* ---- tone: night — warm black ground, cream lines, accent live element ---- */
.drw.night{
  --ink:var(--theme-ground);--dark:var(--theme-accent);--ground:#11110F;--live-text:var(--theme-accent);
  --hair:color-mix(in srgb,var(--theme-ground) 30%,transparent);
  --ink-82:color-mix(in srgb,var(--theme-ground) 92%,transparent);
  --ink-70:color-mix(in srgb,var(--theme-ground) 84%,transparent);
  --ink-60:color-mix(in srgb,var(--theme-ground) 76%,transparent);
  --ink-55:color-mix(in srgb,var(--theme-ground) 70%,transparent);
  --dot:color-mix(in srgb,var(--theme-ground) 12%,transparent);
  background:#11110F;
  background-image:radial-gradient(var(--dot) 1px,transparent 1.6px);
  color:var(--theme-ground);
}
.drw.night::before{background:var(--theme-accent);}
.drw.night .a-fill.paper{fill:#11110F;}
.drw.night .grid-line{stroke-opacity:.1;}
.drw.night .a-frame{stroke-opacity:.85;}
.drw.night .a-line{stroke-opacity:.8;stroke-width:1.15;}
.drw.night .a-line.strong{stroke-opacity:1;stroke-width:1.5;}
.drw.night .a-line.faint{stroke-opacity:.4;}
.drw.night .a-line.hidden-line{stroke-opacity:.55;}
.drw.night .a-fill{fill-opacity:.08;}
.drw.night .a-fill.mid{fill-opacity:.14;}
.drw.night .a-fill.accent{fill-opacity:.2;}
.drw.night .a-hatch{stroke-opacity:.4;}
.drw.night .a-ring{stroke-opacity:.8;stroke-width:1.2;}
.drw.night .a-ring.dash{stroke-opacity:.6;}
.drw.night .a-tick{stroke-opacity:.8;}
.drw.night .a-tick.major{stroke-opacity:1;stroke-width:1.5;}
.drw.night .a-node{fill-opacity:.9;}
.drw.night .a-dim{stroke-opacity:.8;}
.drw.night .lead{stroke-opacity:.85;}
.drw.night .tb-cell{stroke-opacity:.7;}
.drw.night .lead,.drw.blueprint .lead{stroke-opacity:.9;}

/* ---- tone: blueprint — the palette's dark surface, full-strength cream lines.
   --theme-surface-dark rather than --theme-dark: identical for every palette
   whose dark is a surface, and the ink for orange, whose dark (#D2480F) can
   hold no text colour at 4.5:1. ---- */
.drw.blueprint{
  --ink:var(--theme-ground);--dark:var(--theme-accent);--ground:var(--theme-surface-dark);--live-text:var(--theme-accent);
  --hair:color-mix(in srgb,var(--theme-ground) 35%,transparent);
  --ink-82:var(--theme-ground);
  --ink-70:color-mix(in srgb,var(--theme-ground) 90%,transparent);
  --ink-60:color-mix(in srgb,var(--theme-ground) 84%,transparent);
  --ink-55:color-mix(in srgb,var(--theme-ground) 78%,transparent);
  --dot:color-mix(in srgb,var(--theme-ground) 16%,transparent);
  background:var(--theme-surface-dark);
  background-image:radial-gradient(var(--dot) 1px,transparent 1.6px);
  color:var(--theme-ground);
}
.drw.blueprint::before{background:var(--theme-accent);}
.drw.blueprint .a-fill.paper{fill:var(--theme-surface-dark);}
.drw.blueprint .grid-line{stroke-opacity:.16;}
.drw.blueprint .a-frame{stroke-opacity:1;stroke-width:1.2;}
.drw.blueprint .a-frame.inner{stroke-opacity:.6;}
.drw.blueprint .a-crop{stroke-opacity:1;}
.drw.blueprint .a-line{stroke-opacity:.95;stroke-width:1.25;}
.drw.blueprint .a-line.strong{stroke-opacity:1;stroke-width:1.7;}
.drw.blueprint .a-line.faint{stroke-opacity:.55;}
.drw.blueprint .a-line.axis{stroke-opacity:.6;}
.drw.blueprint .a-line.hidden-line{stroke-opacity:.7;}
.drw.blueprint .a-line.accent{stroke-width:1.9;}
.drw.blueprint .a-fill{fill-opacity:.12;}
.drw.blueprint .a-fill.mid{fill-opacity:.2;}
.drw.blueprint .a-fill.accent{fill-opacity:.22;}
.drw.blueprint .a-hatch{stroke-opacity:.6;stroke-width:1;}
.drw.blueprint .a-ring{stroke-opacity:.95;stroke-width:1.3;}
.drw.blueprint .a-ring.dash{stroke-opacity:.75;}
.drw.blueprint .a-ring.faint{stroke-opacity:.55;}
.drw.blueprint .a-tick{stroke-opacity:.95;stroke-width:1.1;}
.drw.blueprint .a-tick.major{stroke-opacity:1;stroke-width:1.6;}
.drw.blueprint .a-node{fill-opacity:1;}
.drw.blueprint .a-dim{stroke-opacity:.95;stroke-width:1;}
.drw.blueprint .lead{stroke-opacity:.95;stroke-width:1.1;}
.drw.blueprint .tb-cell{stroke-opacity:.9;}
`;

/* ------------------------------------------------------------------ */
/*  Sheet — frame, crop marks, construction grid, defs                  */
/* ------------------------------------------------------------------ */

export function Sheet({
  width = 1180,
  height = 620,
  label,
  children,
  style,
}: {
  /** User-space width; 1180 for sheets, 760 for square spot figures. */
  width?: number;
  /** User-space height (620 landscape · 760 tall · 760 for spots). */
  height?: number;
  label: string;
  children: ReactNode;
  style?: CSSProperties;
}) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const w = width;
  const h = height;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      role="img"
      aria-label={label}
      style={style}
    >
      <defs>
        <pattern id={`${uid}-grid`} width="40" height="40" patternUnits="userSpaceOnUse">
          <path className="grid-line" d="M40 0 H0 V40" />
        </pattern>
      </defs>
      <rect x="24" y="24" width={w - 48} height={h - 48} fill={`url(#${uid}-grid)`} />
      <rect className="a-frame" x="16" y="16" width={w - 32} height={h - 32} />
      <rect className="a-frame inner" x="24" y="24" width={w - 48} height={h - 48} />
      <path className="a-crop" d={`M8 24 H28 M24 8 V28 M${w - 28} 24 H${w - 8} M${w - 24} 8 V28 M8 ${h - 24} H28 M24 ${h - 28} V${h - 8} M${w - 28} ${h - 24} H${w - 8} M${w - 24} ${h - 28} V${h - 8}`} />
      {children}
    </svg>
  );
}

/** Each Dim/Leader/Arrow carries its own marker defs (cheap, and always
 *  correct regardless of which Sheet it sits in). */
function useMarkers() {
  return useId().replace(/[^a-zA-Z0-9]/g, "");
}

function ArrowDefs({ uid, accent }: { uid: string; accent?: boolean }) {
  return (
    <defs>
      <marker id={`${uid}-m`} markerWidth="9" markerHeight="9" refX="6.5" refY="4" orient="auto-start-reverse">
        <path d="M0.5 1.5 L6.5 4 L0.5 6.5" style={{ fill: "none", stroke: accent ? "var(--dark)" : "var(--ink)", strokeOpacity: accent ? 0.9 : 0.6 }} strokeWidth={accent ? 1.2 : 1} strokeLinecap="round" strokeLinejoin="round" />
      </marker>
    </defs>
  );
}

/* ------------------------------------------------------------------ */
/*  Hatched region — section fill                                       */
/* ------------------------------------------------------------------ */

export function Hatch({
  x,
  y,
  width,
  height,
  rx,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  rx?: number;
}) {
  const uid = useMarkers();
  return (
    <g>
      <defs>
        <pattern id={`${uid}-h`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <path className="a-hatch" d="M0 0 V6" />
        </pattern>
      </defs>
      <rect x={x} y={y} width={width} height={height} rx={rx} fill={`url(#${uid}-h)`} />
    </g>
  );
}

/* ------------------------------------------------------------------ */
/*  Dimension line                                                      */
/* ------------------------------------------------------------------ */

export function Dim({
  x1,
  y1,
  x2,
  y2,
  label,
  offset = 0,
  accent = false,
  ext = 10,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  label: string;
  /** Perpendicular offset of the dimension line from the measured points. */
  offset?: number;
  accent?: boolean;
  /** Extension-line overshoot past the dimension line. */
  ext?: number;
}) {
  const uid = useMarkers();
  const horizontal = Math.abs(y2 - y1) < Math.abs(x2 - x1);
  const ox = horizontal ? 0 : offset;
  const oy = horizontal ? offset : 0;
  const lx1 = x1 + ox;
  const ly1 = y1 + oy;
  const lx2 = x2 + ox;
  const ly2 = y2 + oy;
  const mx = (lx1 + lx2) / 2;
  const my = (ly1 + ly2) / 2;
  const sign = offset >= 0 ? 1 : -1;
  const extD = horizontal
    ? `M${x1} ${y1} V${ly1 + sign * ext} M${x2} ${y2} V${ly2 + sign * ext}`
    : `M${x1} ${y1} H${lx1 + sign * ext} M${x2} ${y2} H${lx2 + sign * ext}`;
  return (
    <g>
      <ArrowDefs uid={uid} accent={accent} />
      <path className="a-dim" d={extD} />
      <line
        className="a-dim"
        x1={lx1}
        y1={ly1}
        x2={lx2}
        y2={ly2}
        markerStart={`url(#${uid}-m)`}
        markerEnd={`url(#${uid}-m)`}
        style={accent ? { stroke: "var(--dark)", strokeOpacity: 0.9 } : undefined}
      />
      {horizontal ? (
        <text className={`a-dim-fig mono${accent ? " accent" : ""}`} x={mx} y={my - 6} textAnchor="middle">
          {label}
        </text>
      ) : (
        <text
          className={`a-dim-fig mono${accent ? " accent" : ""}`}
          x={mx}
          y={my}
          textAnchor="middle"
          transform={`rotate(-90 ${mx} ${my}) translate(0 -6)`}
        >
          {label}
        </text>
      )}
    </g>
  );
}

/* ------------------------------------------------------------------ */
/*  Leader callout — dot at target, elbow, main + sub label             */
/* ------------------------------------------------------------------ */

export function Leader({
  target,
  at,
  main,
  sub,
  anchor = "start",
  dot = true,
}: {
  /** Point on the drawing being called out. */
  target: [number, number];
  /** Where the label sits (text baseline start). */
  at: [number, number];
  main: string;
  sub?: string;
  anchor?: "start" | "end";
  dot?: boolean;
}) {
  const [tx, ty] = target;
  const [ax, ay] = at;
  const textRef = useRef<SVGTextElement>(null);
  const [measured, setMeasured] = useState<number | null>(null);
  // Measure the label so the shelf underlines exactly the text; the first
  // paint uses an estimate (12px tracked caps ≈ 9.4px/char) and then snaps.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;
    try {
      const w = el.getComputedTextLength();
      if (w > 0) setMeasured(Math.round(w));
    } catch {
      // Not measurable (detached / unsupported) — keep the estimate.
    }
  }, []);
  const span = Math.max(48, measured ?? Math.round(main.length * 9.4));
  const startX = anchor === "start" ? ax : ax - span;
  const endX = startX + span;
  const shelfY = ay + 4;
  // Meet the shelf at the end nearest the target so the diagonal never
  // crosses the text; when the target sits above the label, dog-leg
  // sideways first for the same reason.
  const nearEnd = Math.abs(tx - startX) <= Math.abs(tx - endX) ? startX : endX;
  const above = ty < ay - 16 && tx > startX - 8 && tx < endX + 8;
  const outX = nearEnd === startX ? startX - 28 : endX + 28;
  const diagonal = above
    ? `M${tx} ${ty} H${outX} L${nearEnd} ${shelfY}`
    : `M${tx} ${ty} L${nearEnd} ${shelfY}`;
  return (
    <g>
      {dot ? <circle className="a-core" cx={tx} cy={ty} r="3" /> : null}
      <path className="lead" d={diagonal} />
      <path className="lead-shelf" d={`M${startX} ${shelfY} H${endX}`} />
      <text ref={textRef} className="lead-main" x={ax} y={ay - 2} textAnchor={anchor}>
        {main}
      </text>
      {sub ? (
        <text className="lead-sub" x={ax} y={ay + 16} textAnchor={anchor}>
          {sub}
        </text>
      ) : null}
    </g>
  );
}

/* ------------------------------------------------------------------ */
/*  Flow arrow (single, with arrowhead)                                 */
/* ------------------------------------------------------------------ */

export function Arrow({
  d,
  accent = false,
  animated = false,
  className = "",
}: {
  d: string;
  accent?: boolean;
  animated?: boolean;
  className?: string;
}) {
  const uid = useMarkers();
  return (
    <g>
      <ArrowDefs uid={uid} accent={accent} />
      <path
        className={`a-line${accent ? " accent" : " strong"}${animated ? " flow" : ""} ${className}`}
        d={d}
        markerEnd={`url(#${uid}-m)`}
      />
    </g>
  );
}

/* ------------------------------------------------------------------ */
/*  Title block — drawing-sheet metadata cells                          */
/* ------------------------------------------------------------------ */

export function TitleBlock({
  x,
  y,
  width = 420,
  drawing,
  rows,
}: {
  x: number;
  y: number;
  width?: number;
  /** Sheet title (e.g. "MLX-DRW-003 · The receipt"). */
  drawing: string;
  /** Key/value cells laid out in a row under the title. */
  rows: Array<[string, string, boolean?]>;
}) {
  // Cells share the width in proportion to their longest text so a long
  // value never overruns its cell (10.5px tracked caps ≈ 7.6px/char).
  const weights = rows.map(([k, v]) => Math.max(90, Math.max(k.length, v.length) * 7.6 + 20));
  const total = weights.reduce((a, b) => a + b, 0);
  const widths = weights.map((w) => (w / total) * width);
  const starts = widths.map((_, i) => x + widths.slice(0, i).reduce((a, b) => a + b, 0));
  return (
    <g>
      <rect className="tb-cell" x={x} y={y} width={width} height="24" />
      <text className="tb-head" x={x + 10} y={y + 16}>
        {drawing}
      </text>
      <rect className="tb-cell" x={x} y={y + 24} width={width} height="40" />
      {rows.map(([k, v, strong], i) => (
        <g key={k}>
          {i > 0 ? <line className="tb-cell" x1={starts[i]} y1={y + 24} x2={starts[i]} y2={y + 64} /> : null}
          <text className="tb-key" x={(starts[i] ?? x) + 10} y={y + 39}>
            {k}
          </text>
          <text className={`tb-val${strong ? " strong" : ""}`} x={(starts[i] ?? x) + 10} y={y + 55}>
            {v}
          </text>
        </g>
      ))}
    </g>
  );
}

/* ------------------------------------------------------------------ */
/*  Revision triangle + approval line                                   */
/* ------------------------------------------------------------------ */

/** The drafting revision marker: a small triangle carrying the revision
 *  number, placed against the changed line. */
export function RevisionTriangle({
  x,
  y,
  n,
  size = 12,
}: {
  x: number;
  y: number;
  n: string;
  size?: number;
}) {
  const h = size * 0.9;
  return (
    <g>
      <path className="rev-tri" d={`M${x} ${y + h} L${x + size / 2} ${y} L${x + size} ${y + h} Z`} />
      <text className="rev-tri-num" x={x + size / 2} y={y + h - 2} textAnchor="middle">
        {n}
      </text>
    </g>
  );
}

/**
 * Approval as a drafting sheet records it: a revision triangle, a tracked-caps
 * line ("Approved · v2"), an optional sub-line (date / approver) and a thin
 * rule — no box, no rotation. Replaces the rubber stamp.
 */
export function Approval({
  x,
  y,
  text,
  sub,
  rev = "2",
  width = 168,
}: {
  x: number;
  y: number;
  text: string;
  sub?: string;
  rev?: string;
  width?: number;
}) {
  return (
    <g>
      <RevisionTriangle x={x} y={y} n={rev} />
      <text className="approval-text" x={x + 20} y={y + 10}>
        {text}
      </text>
      {sub ? (
        <text className="approval-sub" x={x + 20} y={y + 23}>
          {sub}
        </text>
      ) : null}
      <path className="approval-rule" d={`M${x} ${y + (sub ? 30 : 17)} H${x + width}`} />
    </g>
  );
}

/* ------------------------------------------------------------------ */
/*  Sheet header — label + sub in the top-left margin                   */
/* ------------------------------------------------------------------ */

export function SheetHeading({
  x = 48,
  y = 54,
  label,
  sub,
}: {
  x?: number;
  y?: number;
  label: string;
  sub: string;
}) {
  return (
    <g>
      <text className="a-label" x={x} y={y}>
        {label}
      </text>
      <text className="a-sub" x={x} y={y + 15}>
        {sub}
      </text>
    </g>
  );
}

/* ------------------------------------------------------------------ */
/*  Tones — the same sheet re-inked                                     */
/* ------------------------------------------------------------------ */

/** `paper` — ink on cream (default). `contrast` — heavy ink on lighter
 *  paper. `dark` / `night` — the system dark treatment: warm-black ground,
 *  cream lines, accent live element. `claret` — cream/accent lines on the
 *  claret surface. `blueprint` — the palette's dark surface, full-strength
 *  cream lines. */
export type PlateTone = "paper" | "contrast" | "dark" | "night" | "blueprint" | "claret";

/** Lets a variant re-render an existing sheet under another tone without
 *  touching the drawing: wrap its `render()` in a ToneContext provider. */
export const ToneContext = createContext<PlateTone | null>(null);

/* ------------------------------------------------------------------ */
/*  Plate wrapper — paper sheet + caption rail under the drawing        */
/* ------------------------------------------------------------------ */

export function Plate({
  children,
  caption,
  figure,
  dark = false,
  spot = false,
  tone,
  motion = true,
}: {
  children: ReactNode;
  /** Right-hand caption on the rail under the sheet. */
  caption: string;
  /** Left-hand figure number, e.g. "Fig. 03". */
  figure: string;
  /** Accent lines on the deep surface instead of ink on paper (= tone "dark"). */
  dark?: boolean;
  /** Square spot figure for cards: centred at max 28rem. */
  spot?: boolean;
  /** Explicit tone; a ToneContext above the plate overrides both props. */
  tone?: PlateTone;
  /** `false` renders the sheet still: every kit animation (`flow`,
   *  `active-arc`, `m-*`) is disabled via the `still` class. The export
   *  pipeline and reduced-motion users rely on it. */
  motion?: boolean;
}) {
  const contextTone = useContext(ToneContext);
  const requested: PlateTone = contextTone ?? tone ?? (dark ? "dark" : "paper");
  // The system's dark treatment is night (warm black); the claret surface
  // remains available explicitly as "claret".
  const resolved = requested === "dark" ? "night" : requested === "claret" ? "dark" : requested;
  const toneClass = resolved === "paper" ? "" : ` ${resolved}`;
  const stillClass = motion ? "" : " still";
  const scrollHintId = useId();
  const plateClassName = `drw${toneClass}${stillClass}${spot ? "" : " sheet drw-scroll"} ${spot ? "p-3 md:p-4" : "p-4 md:p-6"}`;
  return (
    <figure className={spot ? "mx-auto my-0 max-w-[28rem]" : "m-0"}>
      <style>{DRW_CSS}</style>
      {spot ? (
        <div className={plateClassName}>{children}</div>
      ) : (
        <section
          aria-label={`${figure}: ${caption}`}
          aria-describedby={scrollHintId}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: The overflow region needs focus for arrow-key panning.
          tabIndex={0}
          className={plateClassName}
        >
          {children}
        </section>
      )}
      {spot ? null : (
        <p
          id={scrollHintId}
          className={`mt-2 md:hidden ${DATA_LABEL_CLS} text-[color-mix(in_srgb,var(--theme-ink)_78%,transparent)]`}
        >
          Pan sideways to inspect at drawing scale.
        </p>
      )}
      <figcaption className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className={`text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}>{figure}</span>
        <span className="text-[0.9rem] leading-[1.5] text-[color-mix(in_srgb,var(--theme-ink)_82%,transparent)]" style={DISPLAY}>
          {caption}
        </span>
      </figcaption>
    </figure>
  );
}
